/**
 * Uploading a video the app plays back later: a playlist's reference video (`playlists/`) and the
 * video a Sol track link carries (`track-shares/`).
 *
 * - The object is `{folder}/{uploader's id}/{uuid}.{ext}`, with the extension taken from the MIME
 *   type. The original filename never reaches storage, because it is often a client's or a
 *   project's name (D6, F17).
 * - No `public.media` row is written. That table lists the search references kept in search
 *   history. These videos are found through the record that names them, such as a playlist's
 *   `search_metadata.videoPath`.
 * - The uploader's id is the second segment because the backend signs these objects for other
 *   viewers only inside `{folder}/{owner}/` (`in_owner_folder` in the backend's
 *   services/playlist_video_service.py). The storage write rule
 *   (20261012040000_storage_owner_only_writes.sql) lets a user write only into their own folder.
 * - Every video the app takes has one size limit, `FILE_SIZE_LIMITS.VIDEO`. Search applies the
 *   same limit when a video is attached.
 */
import { FILE_SIZE_LIMITS, validateFileSize } from "@/lib/file-validation";
import { supabase } from "@/lib/supabase";

/**
 * The video types the players accept, mapped to the extension the stored object gets. The backend
 * links these extensions (`VIDEO_EXTENSIONS` in services/track_video_link_service.py), signs any
 * object in a playlist owner's folder whatever its extension, and bucket `media` sets no MIME list.
 */
export const VIDEO_UPLOAD_TYPES = {
  "video/mp4": "mp4",
  "video/x-m4v": "m4v",
  "video/quicktime": "mov",
  "video/webm": "webm",
} as const;

/** The types above as a person reads them. */
export const VIDEO_UPLOAD_KINDS = "MP4, M4V, MOV or WebM";

/** A file picker's `accept` for these videos: their MIME types, then their extensions. */
export const VIDEO_UPLOAD_ACCEPT = [
  ...Object.keys(VIDEO_UPLOAD_TYPES),
  ...Object.values(VIDEO_UPLOAD_TYPES).map((extension) => `.${extension}`),
].join(",");

type VideoUploadType = keyof typeof VIDEO_UPLOAD_TYPES;

/** The folders of the `media` bucket that hold videos the app plays back. */
export type VideoUploadFolder = "playlists" | "track-shares";

const MEDIA_BUCKET = "media";

function isVideoUploadType(type: string): type is VideoUploadType {
  // An own-property check: `in` would also accept inherited keys such as "constructor".
  return Object.prototype.hasOwnProperty.call(VIDEO_UPLOAD_TYPES, type);
}

/** Why the app won't take this video, as a sentence to show, or null when it will. */
export function videoUploadProblem(file: File): string | null {
  if (!isVideoUploadType(file.type)) return `Use an ${VIDEO_UPLOAD_KINDS} video.`;
  if (!validateFileSize(file, "video").isValid) {
    return `That video is over ${FILE_SIZE_LIMITS.VIDEO / (1024 * 1024)} MB.`;
  }
  return null;
}

/**
 * Upload `file` into the signed-in user's own folder of the `media` bucket and return its object
 * path. Throws, before anything is uploaded, when no one is signed in or `videoUploadProblem` names
 * a problem. Also throws when the upload fails. Never replaces an existing object.
 */
export async function uploadVideoToMedia(folder: VideoUploadFolder, file: File): Promise<string> {
  const problem = videoUploadProblem(file);
  if (problem) throw new Error(problem);

  const { data } = await supabase.auth.getUser();
  const user = data?.user;
  if (!user) throw new Error("Sign in to upload a video.");

  // `videoUploadProblem` has refused every other type.
  const extension = VIDEO_UPLOAD_TYPES[file.type as VideoUploadType];
  const path = `${folder}/${user.id}/${crypto.randomUUID()}.${extension}`;
  const { error } = await supabase.storage.from(MEDIA_BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type,
    cacheControl: "3600",
  });
  if (error) throw error;
  return path;
}

/**
 * Delete an object `uploadVideoToMedia` made, best-effort, and say whether Storage deleted it.
 * Never throws. Until 20261012030000_media_owner_only_access.sql (N1) gives owners a DELETE policy
 * on `media`, Storage deletes nothing and reports no error, and the answer is false.
 */
export async function deleteUploadedVideo(path: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.storage.from(MEDIA_BUCKET).remove([path]);
    if (error) {
      console.warn("Couldn't delete an uploaded video:", error.message);
      return false;
    }
    // Storage answers with the objects it deleted: none when a policy refused.
    return Array.isArray(data) && data.length > 0;
  } catch (error) {
    console.warn("Couldn't delete an uploaded video:", error);
    return false;
  }
}

/**
 * Whether this browser can show the file's picture. It resolves to true once the metadata loads
 * with a width. It resolves to false on an error, when the file has no picture (audio only), or
 * after `timeoutMs`. The file is read by a detached, muted element through an object URL, and the
 * URL is always revoked.
 *
 * The answer holds for this browser only: an iPhone's HEVC .mov can play in Safari and fail in
 * Chrome. The players therefore keep their own fallback for a video that won't play.
 */
export function probeVideoFile(file: File, timeoutMs = 10_000): Promise<boolean> {
  return new Promise((resolve) => {
    let objectUrl: string;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      resolve(false);
      return;
    }

    const video = document.createElement("video");
    const finish = (playable: boolean) => {
      window.clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      // Emptying the element stops it reading the file before the URL goes.
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(objectUrl);
      resolve(playable);
    };
    const timer = window.setTimeout(() => finish(false), timeoutMs);

    video.muted = true;
    video.preload = "metadata";
    video.onloadedmetadata = () => finish(video.videoWidth > 0);
    video.onerror = () => finish(false);
    video.src = objectUrl;
  });
}
